import { Link } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import { comics, categories, heroFeatures } from '@/mocks/comics';

const stats = [
  { value: '3600+', label: '部连载漫画' },
  { value: '1.2亿', label: '累计阅读量' },
  { value: '24万', label: '日活读者' },
  { value: '4K', label: '最高原图清晰度' },
];

const faqs = [
  {
    q: '高清原图模式如何保证画质？',
    a: '我们为每部作品保留最高可达 4K 的无损原图资源，开启高清原图模式后，系统会按当前屏幕分辨率智能匹配清晰度，放大也不会出现模糊或马赛克。',
  },
  {
    q: '夜间模式与手机系统深色模式冲突吗？',
    a: '夜间模式提供独立亮度与色温调节，可跟随系统自动切换，也可以手动锁定，两者互不干扰，深夜追更更加护眼。',
  },
  {
    q: '双页浏览适合哪些设备使用？',
    a: '双页浏览主要面向横屏的平板和桌面设备，模拟实体单行本的翻页体验；在竖屏手机上会自动切换为单页滚动阅读，避免字小难以辨认。',
  },
];

const testimonials = [
  {
    name: '阿岚',
    role: '漫画老读者',
    avatar: 'https://readdy.ai/api/search-image?query=minimal%20flat%20illustration%20avatar%20of%20a%20friendly%20young%20man%20reading%20a%20book%2C%20warm%20orange%20and%20cream%20color%20palette%2C%20simple%20clean%20style%2C%20soft%20lighting&width=200&height=200&seq=avatar-lan&orientation=squarish',
    quote: '夜间模式做得太舒服了，深夜追更眼睛一点不累，滚动阅读在手上一划到底，体验很顺滑。',
  },
  {
    name: '小北',
    role: '国漫爱好者',
    avatar: 'https://readdy.ai/api/search-image?query=minimal%20flat%20illustration%20avatar%20of%20a%20cheerful%20young%20woman%20with%20glasses%20smiling%2C%20warm%20amber%20and%20cream%20color%20palette%2C%20simple%20clean%20style%2C%20soft%20lighting&width=200&height=200&seq=avatar-bei&orientation=squarish',
    quote: '双页浏览在平板上看分镜太有感觉了，高清原图放大后线条网点都清清楚楚。',
  },
  {
    name: '老K',
    role: '资深漫画编辑',
    avatar: 'https://readdy.ai/api/search-image?query=minimal%20flat%20illustration%20avatar%20of%20a%20calm%20middle%20aged%20man%20with%20short%20beard%2C%20warm%20orange%20and%20cream%20color%20palette%2C%20simple%20clean%20style%2C%20soft%20lighting&width=200&height=200&seq=avatar-k&orientation=squarish',
    quote: '作为从业者，很看重阅读模式的细节打磨，漫蛙在缩放和翻页手感上的用心让我很认可。',
  },
];

export default function Home() {
  useSeo({
    title: '漫蛙漫画网 - 高清原图、夜间模式、双页浏览与滚动阅读体验',
    description: '漫蛙漫画网首页提供多种阅读模式，包括高清原图、双页浏览、夜间模式、缩放设置及滚动阅读，优化你的漫画阅读体验，支持电脑、平板与手机。',
  });
  const featured = comics.filter((c) => c.featured).slice(0, 4);

  return (
    <div>
      {/* Hero */}
      <section className="relative flex min-h-[640px] items-center overflow-hidden bg-background-950 md:min-h-[720px]">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20artistic%20manga%20illustration%20of%20a%20young%20reader%20surrounded%20by%20floating%20comic%20panels%20and%20storyboards%20under%20warm%20amber%20light%2C%20dark%20cinematic%20background%2C%20bold%20dynamic%20composition%2C%20vibrant%20orange%20and%20deep%20charcoal%20tones%2C%20dramatic%20atmosphere%2C%20high%20detail%20digital%20painting&width=1600&height=1000&seq=hero-manwa&orientation=landscape"
          alt="漫蛙漫画网沉浸式漫画阅读背景插画"
          className="absolute inset-0 h-full w-full object-cover object-center opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-28 md:px-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/20 px-4 py-1.5 text-sm font-medium text-accent-200 ring-1 ring-accent-400/40">
              <i className="ri-sparkling-2-fill"></i>
              为每一位漫画读者而设计
            </span>
            <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              把每一格画面，<br />
              读到极致清晰
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-background-200 md:text-lg">
              漫蛙漫画网为你提供高清原图、双页浏览、夜间模式、缩放设置与滚动阅读等多种阅读模式，
              在电脑、平板和手机上都获得流畅舒适的原画级体验。
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/comics"
                className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-center text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
              >
                立即开始阅读
              </Link>
              <Link
                to="/reading-modes"
                className="whitespace-nowrap rounded-full border border-background-600 px-7 py-3 text-center text-sm font-medium text-background-100 transition-colors hover:bg-background-800"
              >
                了解阅读模式
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-background-300">
          <a href="#modes" className="flex flex-col items-center gap-1 text-xs" aria-label="向下滚动查看阅读模式">
            向下探索
            <i className="ri-arrow-down-line animate-bounce text-lg"></i>
          </a>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-background-200 bg-background-50">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-8 px-4 py-10 md:grid-cols-4 md:px-6 md:py-14">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{s.value}</p>
              <p className="mt-1 text-sm text-foreground-600">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reading modes */}
      <section id="modes" className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div className="max-w-xl">
              <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">阅读模式</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                五种阅读模式，随你掌控画面
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                针对不同作品、设备与场景优化阅读方式，把选择权交给每一位读者。
              </p>
            </div>
            <Link
              to="/reading-modes"
              className="whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              查看全部模式 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {heroFeatures.map((f) => (
              <article
                key={f.key}
                className="group rounded-lg border border-background-200 bg-background-100 p-6 transition-colors hover:border-primary-300"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500/15 text-xl text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={f.icon}></i>
                </div>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
              </article>
            ))}
            <Link
              to="/reading-modes"
              className="flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-primary-300 bg-primary-500/5 p-6 text-center transition-colors hover:bg-primary-500/10"
            >
              <i className="ri-arrow-right-circle-line text-3xl text-primary-600"></i>
              <span className="text-sm font-medium text-foreground-700">探索更多阅读细节</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured comics */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-accent-600">本周精选</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">热门连载推荐</h2>
            </div>
            <Link
              to="/comics"
              className="whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-50"
            >
              浏览全部漫画 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-5 lg:grid-cols-4">
            {featured.map((c) => (
              <Link
                to="/comics"
                key={c.id}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-transform hover:-translate-y-1"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={c.cover}
                    alt={`${c.title}漫画封面`}
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-950/80 px-2.5 py-1 text-xs text-background-50">
                    {c.status}
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                    {c.score}分
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="truncate font-heading text-base font-bold text-foreground-950">{c.title}</h3>
                  <p className="mt-1 truncate text-xs text-foreground-600">{c.intro}</p>
                  <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
                    <span>{c.category} · 已更新{c.chapters}话</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-3-fill text-accent-500"></i>
                      {c.likes}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">分类浏览</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">按喜好快速找到作品</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              无论热血冒险、玄幻修仙，还是悬疑推理、甜蜜恋爱，这里都有适合你的故事。
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
            {categories.filter((cat) => cat !== '全部').map((cat, i) => (
              <Link
                to="/comics"
                key={cat}
                className="group flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-6 text-center transition-colors hover:border-primary-300 hover:bg-background-50"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500/15 text-2xl text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={['ri-fire-fill', 'ri-rocket-2-fill', 'ri-magic-fill', 'ri-spy-fill', 'ri-heart-fill', 'ri-cup-line'][i]}></i>
                </span>
                <span className="text-sm font-semibold text-foreground-950">{cat}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent-600">读者口碑</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">他们都在漫蛙阅读</h2>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="flex items-center gap-1 text-primary-500">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">“{t.quote}”</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <img src={t.avatar} alt={`${t.name}的头像`} className="h-11 w-11 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">常见问题</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                阅读中的疑问，这里都有答案
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                关于阅读模式、画质与设备适配的问题，都可以在帮助中心找到详细说明。
              </p>
              <Link
                to="/faq"
                className="mt-6 inline-flex whitespace-nowrap items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
              >
                前往帮助中心 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="space-y-4">
                {faqs.map((f) => (
                  <details
                    key={f.q}
                    className="group rounded-lg border border-background-200 bg-background-100 open:bg-background-50"
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-sm font-semibold text-foreground-950 md:text-base">
                      {f.q}
                      <i className="ri-add-line text-xl text-primary-600 transition-transform group-open:rotate-45"></i>
                    </summary>
                    <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-950">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <div className="flex flex-col items-center justify-between gap-6 rounded-2xl bg-gradient-to-br from-primary-700 via-primary-600 to-accent-500 px-8 py-10 md:flex-row md:px-12">
            <div>
              <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                现在开启你的漫蛙之旅
              </h2>
              <p className="mt-2 text-sm text-primary-50/90 md:text-base">
                切换阅读模式，把喜欢的漫画读到极致清晰。
              </p>
            </div>
            <Link
              to="/comics"
              className="whitespace-nowrap rounded-full bg-background-950 px-7 py-3 text-sm font-semibold text-background-50 transition-opacity hover:opacity-90"
            >
              免费开始阅读
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}